import { z } from "zod";

/**
 * `CLIENT-PROFILE-RETURN` (`P0-DOS-075`): **the client's declared profile, `GET` and `PUT /api/v1/me/profile`: one contract for the door and for every client** (A48: no client reads the answer untyped).
 * Both doors answer `{ data }`; the `PUT` answers `{ data, notices }` too, `notices` being the sentences the save serves back (A47, client side): today the one that says a file of his went back to `analyse`
 * because he changed his declared commitments (« Votre dossier repasse à l'analyse pour tenir compte de votre modification. »), one per file returned, `[]` when none was.
 *
 * Strict on purpose (an extra key is a mismatch, for stubs and the OpenAPI parity spec), with its lenient READ twin built from the same shapes ({@link buildSchemas}, the `bank-decision-answer.ts` pattern): the web
 * client parses the live answer with the twin, so an unforeseen key is tolerated, a missing required field or a wrong type fails loudly. The door does not validate at runtime.
 */
export const PROFILE_CONTRACT_TYPES = [
  "fonctionnaireTitulaire",
  "cdi",
  "cdd",
  "contractuelEtat",
  "autre",
] as const;
export const PROFILE_CREDIT_TYPES = ["pretBancaire", "microfinance", "avanceEmployeur"] as const;

function buildSchemas(object: typeof z.strictObject) {
  const text = z.string();
  const employmentSchema = object({
    id: text,
    employer: text,
    position: text,
    contractType: z.enum(PROFILE_CONTRACT_TYPES),
    dateOfHire: text.nullable(),
    declaredSeniorityMonths: z.number().int().nullable(),
    monthlyNetSalary: z.number().int(),
  });
  const commitmentSchema = object({
    id: text,
    lenderName: text,
    creditType: z.enum(PROFILE_CREDIT_TYPES),
    outstandingBalance: z.number().int(),
    balanceAsOfDate: text,
    monthlyInstalment: z.number().int(),
    loanStartDate: text.nullable(),
    loanExpectedEndDate: text.nullable(),
  });
  const clientProfileSchema = object({
    fullName: text,
    phone: text,
    city: text.nullable(),
    dateOfBirth: text.nullable(),
    declaredAddress: text.nullable(),
    employments: z.array(employmentSchema),
    commitments: z.array(commitmentSchema),
    noCommitmentsDeclared: z.boolean(),
    commitmentsSection: z.enum(["unfilled", "declared", "none"]),
  });
  return {
    clientProfileSchema,
    clientProfileReadAnswerSchema: object({ data: clientProfileSchema }),
    clientProfileWriteAnswerSchema: object({ data: clientProfileSchema, notices: z.array(text) }),
  };
}

const strict = buildSchemas(z.strictObject);
const loose = buildSchemas(z.looseObject);

export const clientProfileSchema = strict.clientProfileSchema;
/** `GET /api/v1/me/profile`. */
export const clientProfileReadAnswerSchema = strict.clientProfileReadAnswerSchema;
/** `PUT /api/v1/me/profile`. */
export const clientProfileWriteAnswerSchema = strict.clientProfileWriteAnswerSchema;
/** The READ twins: extra keys tolerated at every level, every required field and type identical. */
export const clientProfileReadAnswerLenientSchema = loose.clientProfileReadAnswerSchema;
export const clientProfileWriteAnswerLenientSchema = loose.clientProfileWriteAnswerSchema;

export type ClientProfileAnswerData = z.infer<typeof clientProfileSchema>;
export type ClientProfileReadAnswer = z.infer<typeof clientProfileReadAnswerSchema>;
export type ClientProfileWriteAnswer = z.infer<typeof clientProfileWriteAnswerSchema>;
